import React from 'react'
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { CheckCircle, Cancel } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import type { PasswordValidationResult } from '@/lib/utils/passwordValidation'
import { validatePassword } from '@/lib/utils/passwordValidation'

interface PasswordStrengthIndicatorProps {
    password: string
    showRequirements?: boolean
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
    password,
    showRequirements = true,
}) => {
    const { t } = useTranslation()
    const validation: PasswordValidationResult = validatePassword(password)

    if (!showRequirements || password.length === 0) {
        return null
    }

    return (
        <Box
            sx={{
                mt: 1,
                p: 2,
                backgroundColor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
            }}
            role="region"
            aria-label={t('auth:passwordStrength.title')}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    mb: 1,
                }}
            >
                {t('auth:passwordStrength.title')}
            </Typography>
            <List dense sx={{ py: 0 }}>
                {Object.entries(validation.requirements).map(([key, requirement]) => (
                    <ListItem key={key} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            {requirement.met ? (
                                <CheckCircle
                                    color="success"
                                    fontSize="small"
                                    aria-label={t('auth:passwordStrength.meets')}
                                />
                            ) : (
                                <Cancel
                                    color="error"
                                    fontSize="small"
                                    aria-label={t('auth:passwordStrength.missing')}
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography
                                    variant="body2"
                                    color={requirement.met ? 'success.main' : 'error.main'}
                                    sx={{
                                        textDecoration: requirement.met ? 'none' : 'line-through',
                                        opacity: requirement.met ? 1 : 0.7,
                                    }}
                                >
                                    {t(`auth:passwordStrength.${requirement.label}`)}
                                </Typography>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    )
}

export default PasswordStrengthIndicator
